import './MinuteByMinute.less';

import React, { useEffect } from 'react';
import clsx from 'clsx';
import { SvgIcon } from '../../../../icons/SvgIcon';
import {
    detailMbmById$,
    detailMbmIds$,
    detailMbmsByPeriod$,
    getFirstMbmByPeriodOrder$,
    getRainDelay$,
    hasMbmPeriods$,
    mbmMatch$
} from '../../push/detail/subs';
import { useReframe } from 'nike-re-framejs';
import { lang, localize, localizeMap } from '../../utils';
import { sportLayout$$ } from '../../push/overview/subs';
import { formatTimer } from '../../timer';
import { TimerFormat } from '../../dto';
import Tabs, { useTabs } from '../Tabs';
import { isMobile } from 'react-device-detect';
import i18n from '../../../../common/i18n';
import { SportIconNui } from '../../../../icons/SportIconNui';

const CENTERED_SCORE_TYPES = ['SCORE', 'TENIS_SCORE_GAME', 'SET_SCORE', 'GAME_SCORE', 'LEG_SCORE'];

function MinuteByMinute({ matchId, sportCode, sportName }: { matchId: string; sportCode: string; sportName?: string }) {
    const { subscribe } = useReframe();
    const oldMbmOrderIds = subscribe(detailMbmIds$(matchId));
    const mbmPeriods = subscribe(hasMbmPeriods$);
    const match = subscribe(mbmMatch$(matchId, sportName || sportCode));
    const home = localize(match?.home) || match?.opponents?.home || match?.participants?.[0];
    const away = localize(match?.away) || match?.opponents?.away || match?.participants?.[1];
    const [onTabClick, setArrowVisible, wrapRef, scrollElementRef, active, leftArrow, rightArrow] = useTabs(null);
    const tlvMbmIdsByPeriod = subscribe(detailMbmsByPeriod$(matchId, active)) || oldMbmOrderIds;
    const sportLayout = subscribe(sportLayout$$(sportName || sportCode)) || sportCode;
    //sportname in infocentrum reframe is set only if event from tlv is set, so start tlv mbm subs only in this case turbo.live.mbm.empty
    useEffect(() => {
        if ((mbmPeriods?.length && !active) || (mbmPeriods && !mbmPeriods.includes(active))) {
            onTabClick(null, mbmPeriods[mbmPeriods.length - 1]);
        }
    }, [mbmPeriods, active, onTabClick]);
    if ((!tlvMbmIdsByPeriod && !mbmPeriods) || !match) {
        return <MBMLoader />;
    }

    if (
        (tlvMbmIdsByPeriod && tlvMbmIdsByPeriod?.length === 0) ||
        (!oldMbmOrderIds && mbmPeriods?.length === 0) ||
        !match
    ) {
        return (
            <div className="flex-center c-black-50 py-20 px-20 text-center fs-14">{i18n('turbo.live.mbm.empty')}</div>
        );
    }

    return (
        <div className="fs-14">
            {mbmPeriods ? (
                <Tabs
                    otherClass="px-5 minute-by-minute-tabs br-b-default"
                    defaultColor={false}
                    setArrowVisible={setArrowVisible}
                    wrapRef={wrapRef}
                    leftArrowVisible={leftArrow}
                    rightArrowVisible={rightArrow}
                    transparent
                >
                    {mbmPeriods.map(periodOrder => (
                        <MBMPeriod
                            key={periodOrder}
                            periodOrder={periodOrder}
                            matchId={matchId}
                            activePeriod={active}
                            setActivePeriod={onTabClick}
                            scrollElementRef={scrollElementRef}
                        />
                    ))}
                </Tabs>
            ) : null}

            <div className="flex px-20 pt-10 text-extra-bold c-black-50">
                <div className="flex-1 ellipsis pr-10">{home}</div>
                <div className="flex-1 ellipsis pl-10 text-right">{away}</div>
            </div>

            <div className="flex items-center flex-col ">
                <div className="p-rel full-width bg-white-ng mt-10 mb-10">
                    <PeriodMbmWrapper
                        tlvMbmIdsByPeriod={tlvMbmIdsByPeriod}
                        match={match}
                        matchId={matchId}
                        sportLayout={sportLayout}
                        home={home}
                        away={away}
                    />
                    <div className="minute-by-minute-separator-line p-abs full-height" />
                </div>
            </div>
        </div>
    );
}

function PeriodMbmWrapper({ tlvMbmIdsByPeriod, match, matchId, sportLayout, home, away }) {
    const derefSub = useReframe();
    const rainDelay = derefSub(getRainDelay$(matchId));

    return (
        <>
            {rainDelay && <RainDelayInfo rainDelayInfo={rainDelay} />}
            {tlvMbmIdsByPeriod?.map(mbmTick => (
                <EventInfoRow
                    key={mbmTick?.id || mbmTick}
                    mbmTick={mbmTick}
                    sportCode={sportLayout}
                    matchId={matchId}
                    match={match}
                    home={home}
                    away={away}
                />
            ))}
        </>
    );
}

function MBMPeriod({ periodOrder, matchId, activePeriod, setActivePeriod, scrollElementRef }) {
    const derefSub = useReframe();
    const mbmPeriod = derefSub(getFirstMbmByPeriodOrder$(matchId, periodOrder));
    if (!mbmPeriod) {
        return null;
    }
    const periodName = localizeMap(mbmPeriod.get('period'));
    return (
        <button
            ref={activePeriod === periodOrder ? scrollElementRef : null}
            onClick={() => setActivePeriod(null, periodOrder)}
            className={clsx('px-15 hover-underline c-black-50 no-wrap', {
                'c-black-100 minute-by-minute-period-button-active ': activePeriod === periodOrder,
                'py-30': isMobile,
                'py-20': !isMobile
            })}
        >
            {periodName}
        </button>
    );
}

function MBMLoader() {
    return (
        <div className="bg-black-3 fs-14">
            <div className="flex px-20 py-20">
                <div
                    className="flex-1 pr-10 bg-white-ng"
                    style={{ height: '21px', borderRadius: '4px', marginRight: '40px' }}
                />
                <div className="flex-1 pl-10 bg-white-ng" style={{ height: '21px', borderRadius: '4px' }} />
            </div>

            <div className="flex items-center flex-col ">
                <div className="p-rel full-width bg-white-ng mt-10 mb-10">
                    <div className="minute-by-minute-separator-line p-abs full-height" />
                    {Array.from({ length: 7 }).map((v, index) => (
                        <div className="items-center full-width flex justify-center bg-white-ng my-15" key={index}>
                            <div className="p-rel flex items-center full-width">
                                <div
                                    className="bg-black-3 flex flex-1 items-center"
                                    style={{ marginLeft: '50px', height: '21px', borderRadius: '4px' }}
                                />
                                <span className={'mx-10'}>
                                    <div className="minute-by-minute-separator-orb p-rel" />
                                </span>
                                <div
                                    className="bg-black-3 flex flex-1 items-center"
                                    style={{ marginRight: '50px', height: '21px', borderRadius: '4px' }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const HOME = 'HOME';
const AWAY = 'AWAY';

function RainDelayInfo({ rainDelayInfo }) {
    const info = rainDelayInfo.getIn(['info', lang]) || rainDelayInfo.get('info') || '';
    const period = rainDelayInfo.getIn(['period', lang]);

    return (
        <div className="c-black-100 bg-black-10 py-10 text-center bg-black-3 minute-by-minute-highlited-info p-rel">
            <div className="text-extra-bold">{info}</div>
            <div className="c-black-50 fs-12">
                {period} - {info}
            </div>
        </div>
    );
}

function EventInfoRow({ mbmTick, sportCode, matchId, match, home, away }) {
    const derefSub = useReframe();
    const mbm = derefSub(detailMbmById$(matchId, mbmTick?.id || mbmTick));
    if (!mbm) {
        return null;
    }
    const info = mbm.getIn(['info', lang]) || mbm.get('info') || '';
    const participant = mbm.get('participant') || mbm.get('team') || mbm.get('player');
    const score = mbm.get('score');
    const scoreHome = score?.get('home') || mbm.get('scoreHome');
    const scoreAway = score?.get('away') || mbm.get('scoreAway');
    const type = mbm.get('type')?.toUpperCase();
    const period = mbm.getIn(['period', lang]);
    const sportId = match?.sportId;

    if (type === 'RAIN_DELAY') {
        return null;
    }

    if (!participant || type === 'END_OF_PERIOD') {
        const periodScoreInfo = scoreAway && scoreHome + ':' + scoreAway;
        return (
            <div className="c-black-100 bg-black-10 py-10 text-center bg-black-3 minute-by-minute-highlited-info p-rel">
                <div className="text-extra-bold">
                    {info} {periodScoreInfo}
                </div>
                <div className="c-black-50 fs-12">{period && info}</div>
            </div>
        );
    }

    return (
        <div className="items-center full-width flex justify-center bg-white-ng my-15">
            <div className="p-rel flex items-center full-width">
                <EventInfoSide
                    participant={home}
                    info={info}
                    side={HOME}
                    type={type}
                    mbm={mbm}
                    sportCode={sportCode}
                    isParticipantEvent={
                        mbm.get('participant')
                            ? mbm.get('participant') === HOME
                            : participant === match?.opponents?.home
                    }
                    sportId={sportId}
                />
                {!CENTERED_SCORE_TYPES.includes(type) && (
                    <span className={'mx-10'}>
                        <div className="minute-by-minute-separator-orb p-rel" />
                    </span>
                )}
                <EventInfoSide
                    participant={away}
                    info={info}
                    side={AWAY}
                    type={type}
                    mbm={mbm}
                    sportCode={sportCode}
                    isParticipantEvent={
                        mbm.get('participant')
                            ? mbm.get('participant') === AWAY
                            : participant === match?.opponents?.away
                    }
                    sportId={sportId}
                />
            </div>
        </div>
    );
}

const SCORE_TYPES = ['LEG_SCORE', 'GAME_SCORE', 'SET_SCORE', 'SCORE'];

function EventInfoSide({ side, participant, type, info, mbm, sportCode, isParticipantEvent, sportId }) {
    const timeSeconds = mbm.get('timeSeconds');
    const oldTimeMinutes = mbm.get('time');
    const oldTime = mbm.get('timeSec');
    const player = mbm.get('player') || '';
    const isLongTimeFormat = sportCode === 'hokej';
    const formatedTime = formatTimer(oldTime || timeSeconds, isLongTimeFormat ? TimerFormat.MM_SS : TimerFormat.MM);
    const score = mbm.get('score');
    const scoreHome = score?.get('home') || mbm.get('scoreHome');
    const scoreAway = score?.get('away') || mbm.get('scoreAway');

    function getInfoText() {
        if ((type === 'TENIS_SCORE_POINT' || type === 'TENIS_SCORE_GAME') && isParticipantEvent) {
            return <TenisScorePoint mbm={mbm} side={side} />;
        }
        if ((type === 'YELLOWCARD' || type === 'REDCARD') && isParticipantEvent) {
            return `${scoreHome}:${scoreAway}`;
        }
        if (SCORE_TYPES.includes(type?.toUpperCase()) && isParticipantEvent) {
            return (
                <div className="flex items-center">
                    {side === HOME && <span className="pr-10">{player}</span>}
                    <SportIconNui
                        sportId={sportId}
                        sizeX="14"
                        sizeY="14"
                        className={clsx(`c-black-100 flex-shrink-0`)}
                    />
                    {side === AWAY && <span className="pl-10">{player}</span>}
                </div>
            );
        }
        if (isParticipantEvent) {
            return info || '';
        }
        if (formatedTime || (oldTimeMinutes && !(type === 'TENIS_SCORE_POINT' || type === 'TENIS_SCORE_GAME'))) {
            return formatedTime || `${oldTimeMinutes}’`;
        }
        return '';
    }

    return (
        <div
            className={clsx('flex flex-1 items-center text-right', {
                'c-black-50': !isParticipantEvent,
                'c-black-100': isParticipantEvent,
                'minute-by-minute-event-other': CENTERED_SCORE_TYPES.includes(type) && !isParticipantEvent,
                'minute-by-minute-event-full mx-10': CENTERED_SCORE_TYPES.includes(type) && isParticipantEvent,
                'justify-end': side === HOME,
                'justify-start': side === AWAY
            })}
        >
            {side === AWAY && isParticipantEvent && getItemByType(type, participant, mbm, sportId, 'mr-10')}
            <div
                className={clsx({
                    'mr-10': CENTERED_SCORE_TYPES.includes(type) && isParticipantEvent && side === HOME,
                    'ml-10': CENTERED_SCORE_TYPES.includes(type) && isParticipantEvent && side === AWAY,
                    'text-left pr-10': side === AWAY,
                    'text-right pl-10': side === HOME,
                    'full-width': type === 'TENIS_SCORE_GAME' || type === 'TENIS_SCORE_POINT'
                })}
            >
                {getInfoText()}
            </div>
            {side === HOME && isParticipantEvent && getItemByType(type, participant, mbm, sportId, 'ml-10')}
        </div>
    );
}

function getItemByType(type, opponent, mbm, sportId, spaceClass) {
    switch (type) {
        case 'YELLOWCARD':
            return <div className={clsx('minute-by-minute-card', spaceClass)} />;
        case 'REDCARD':
            return <div className={clsx('minute-by-minute-card minute-by-minute-card-red', spaceClass)} />;
        case 'CORNER':
            return (
                <div>
                    <SvgIcon
                        imgId="corner-kick"
                        sizeX="14px"
                        sizeY="14px"
                        title="corner-kick-icon"
                        className={clsx('c-black-100', spaceClass)}
                    />
                </div>
            );
        case 'LEG_SCORE':
        case 'GAME_SCORE':
        case 'SET_SCORE':
        case 'TENIS_SCORE_GAME':
        case 'SCORE':
            return <EventInfoScore mbm={mbm} sportId={sportId} />;
        default:
            return null;
    }
}

function EventInfoScore({ mbm, sportId }) {
    const score = mbm.get('score');
    const scoreHome = score?.get('home') || mbm.get('scoreHome');
    const scoreAway = score?.get('away') || mbm.get('scoreAway');

    return (
        <div className={`minute-by-minute-score br-radius-xxxs fs-12 text-extra-bold bg-${sportId} p-rel c-white px-5`}>
            {scoreHome}:{scoreAway}
        </div>
    );
}

//dont event try, but if you try - have fun
//TODO: az budem v lepsom psychickom rozpolozeni skusit to refactornut
function TenisScorePoint({ mbm, side }) {
    const participant = mbm.get('participant');
    const point = participant ? mbm.getIn(['point', lang]) : mbm.get('info');
    const score = mbm.get('score');
    const scoreHome = score?.get('home') || mbm.get('scoreHome');
    const scoreAway = score?.get('away') || mbm.get('scoreAway');
    const type = mbm.get('type').toUpperCase();
    const info = mbm.getIn(['info', lang]) || (!participant && mbm.get('info')) || '';
    return (
        <div className="flex">
            <span
                className={clsx('flex-1', {
                    'c-black-50': side === HOME && type !== 'TENIS_SCORE_GAME',
                    'text-extra-bold': type === 'TENIS_SCORE_GAME',
                    'minute-by-minute-tenis-score-game': type === 'TENIS_SCORE_GAME' && side !== HOME,
                    'minute-by-minute-event-full': type === 'TENIS_SCORE_GAME' && side === HOME
                })}
            >
                {type === 'TENIS_SCORE_POINT'
                    ? side === HOME
                        ? `${scoreHome}:${scoreAway}`
                        : point
                    : side === HOME
                    ? info
                    : ''}
            </span>
            <span
                className={clsx('flex-1', {
                    'c-black-50': side !== HOME && type !== 'TENIS_SCORE_GAME',
                    'text-extra-bold': type === 'TENIS_SCORE_GAME',
                    'minute-by-minute-tenis-score-game': type === 'TENIS_SCORE_GAME' && side === HOME,
                    'minute-by-minute-event-full': type === 'TENIS_SCORE_GAME' && side !== HOME
                })}
            >
                {type === 'TENIS_SCORE_POINT'
                    ? side === HOME
                        ? point
                        : `${scoreHome}:${scoreAway}`
                    : side === HOME
                    ? ''
                    : info}
            </span>
        </div>
    );
}

export default MinuteByMinute;
